import { Alert, Statistic, Table } from "antd";
import { FinanceEntryType } from "../typeDefs";
import { getPersonalApportionmentAmount } from "./financeApportionment";
import { GST_REGISTRATION_LABEL, isInGstRegisteredPeriod, requiresGstDetails } from "./taxConfig";

const COMPANY_TAX_RATE = 0.28;
const PROVISIONAL_TAX_THRESHOLD = 5000;

const money = (value: number) => `$${value.toFixed(2)}`;
const gstPortion = (amount: number) => (amount * 3) / 23;

const getGstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstIncluded || !entry.gstRegistered) return 0;

  const gst = gstPortion(entry.amount);

  if (entry.category === "Expenses" || entry.category === "Assets") {
    const apportionRate = entry.apportionMethod === "None" ? 0 : entry.apportionRate ?? 100;
    return gst * (apportionRate / 100);
  }

  return gst;
};

export const BusinessTaxDashboard = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const entries = data
    .filter((item) => !item.isDeleted)
    .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]);

  const revenueEntries = entries.filter((item) => item.category === "Revenue");
  const expenseEntries = entries.filter((item) => item.category === "Expenses");
  const assetEntries = entries.filter((item) => item.category === "Assets");
  const equityEntries = entries.filter((item) => item.category === "Equity");

  const revenue = revenueEntries.reduce((total, item) => total + item.amount, 0);
  const operatingExpenses = expenseEntries.reduce((total, item) => total + item.amount, 0);
  const assetPurchases = assetEntries.reduce((total, item) => total + item.amount, 0);
  const directDrawings = equityEntries
    .filter((item) => item.subCategory === "Shareholder Drawings")
    .reduce((total, item) => total + item.amount, 0);
  const apportionedPersonalDrawings = [...expenseEntries, ...assetEntries].reduce(
    (total, item) => total + getPersonalApportionmentAmount(item),
    0,
  );
  const drawings = directDrawings + apportionedPersonalDrawings;

  // Conservative estimate: assets are shown separately and not automatically
  // deducted from taxable profit here unless they have been entered as expenses
  // or depreciation entries. Accountant should confirm depreciation treatment.
  const estimatedTaxableProfit = Math.max(0, revenue - operatingExpenses);
  const estimatedCompanyTax = estimatedTaxableProfit * COMPANY_TAX_RATE;
  const profitAfterCompanyTax = estimatedTaxableProfit - estimatedCompanyTax;

  const gstCollected = revenueEntries.reduce((total, item) => total + getGstAmount(item), 0);
  const gstClaimable = [...expenseEntries, ...assetEntries].reduce(
    (total, item) => total + getGstAmount(item),
    0,
  );
  const netGst = gstCollected - gstClaimable;

  const likelyProvisionalTax =
    estimatedCompanyTax > PROVISIONAL_TAX_THRESHOLD ? estimatedCompanyTax * 1.05 : 0;

  const missingReceipts = [...expenseEntries, ...assetEntries].filter((item) => !item.receipt);
  const missingGst = entries.filter(
    (item) =>
      requiresGstDetails(item) &&
      (typeof item.gstIncluded !== "boolean" || typeof item.gstRegistered !== "boolean"),
  );

  const summaryRows = [
    { key: "revenue", label: "Revenue", amount: revenue, note: "Business income for selected period" },
    {
      key: "expenses",
      label: "Operating expenses",
      amount: operatingExpenses,
      note: "Expense category entries only",
    },
    {
      key: "profit",
      label: "Estimated taxable profit",
      amount: estimatedTaxableProfit,
      note: "Revenue minus operating expenses; assets excluded unless depreciated/expensed",
    },
    {
      key: "companyTax",
      label: "Estimated company income tax",
      amount: estimatedCompanyTax,
      note: "Estimated at 28% NZ company tax rate",
    },
    {
      key: "afterTax",
      label: "Estimated profit after company tax",
      amount: profitAfterCompanyTax,
      note: "Before dividends, drawings treatment, and accountant adjustments",
    },
    {
      key: "gst",
      label: netGst >= 0 ? "GST payable" : "GST refundable",
      amount: Math.abs(netGst),
      note: "GST collected less GST claimable",
    },
    {
      key: "provisional",
      label: "Possible provisional tax planning amount",
      amount: likelyProvisionalTax,
      note: "105% uplift estimate if residual income tax is over $5,000",
    },
    {
      key: "assets",
      label: "Asset purchases",
      amount: assetPurchases,
      note: "Shown separately; check depreciation treatment",
    },
    {
      key: "drawings",
      label: "Shareholder drawings",
      amount: drawings,
      note: "Equity drawings plus personal portion of split/apportioned business-account spend",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Alert
        type="info"
        showIcon
        message="Business tax estimate"
        description={`This is a planning estimate only. It uses the selected date range and assumes a 28% NZ company tax rate. GST is only included from the company's GST registration date (${GST_REGISTRATION_LABEL}). Accountant adjustments may change taxable profit, depreciation, shareholder salary/drawings, and provisional tax.`}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
        <Statistic title="Revenue" value={revenue} precision={2} prefix="$" />
        <Statistic title="Operating expenses" value={operatingExpenses} precision={2} prefix="$" />
        <Statistic title="Estimated taxable profit" value={estimatedTaxableProfit} precision={2} prefix="$" />
        <Statistic title="Company tax estimate" value={estimatedCompanyTax} precision={2} prefix="$" />
        <Statistic
          title={netGst >= 0 ? "GST to pay" : "GST owed/refundable"}
          value={Math.abs(netGst)}
          precision={2}
          prefix="$"
          valueStyle={{ color: netGst >= 0 ? "#cf1322" : "#3f8600" }}
        />
        <Statistic title="Possible provisional tax" value={likelyProvisionalTax} precision={2} prefix="$" />
      </div>

      {(missingReceipts.length > 0 || missingGst.length > 0) && (
        <Alert
          type="warning"
          showIcon
          message="Review required before relying on this estimate"
          description={`${missingReceipts.length} expense/asset entries are missing receipts. ${missingGst.length} entries are missing GST details.`}
        />
      )}

      <Table
        title={() => "Business tax estimate breakdown"}
        size="small"
        pagination={false}
        dataSource={summaryRows}
        columns={[
          { title: "Item", dataIndex: "label" },
          { title: "Amount", dataIndex: "amount", render: money },
          { title: "Notes", dataIndex: "note" },
        ]}
      />
    </div>
  );
};
